import dayjs from 'dayjs';
import { Contact, LabelGroupsResponse, TargetType } from './types.ts';
import { QueryClient } from '@tanstack/react-query';
import { GPanelContactsKeys } from '../gpanel-contacts-api.ts';

export function getDateStringWithFromNow(timestamp: number) {
  const date = dayjs(timestamp).format('MM/DD/YY');
  const days = dayjs(timestamp).fromNow();
  return `${date} (${days})`;
}

export function getDateTimeString(timestamp: Date | number) {
  return dayjs(timestamp).format('MM/DD/YYYY hh:mm:ssA');
}

export function getTargetTypeLabel(targetType: TargetType) {
  return {
    User: 'User',
    Group: 'Group',
    Domain: 'Domain',
    OrgUnit: 'Org Unit',
  }[targetType];
}

export const updateContactLabelCachesOnAdd = (
  queryClient: QueryClient,
  contactIds: number[],
  labelIds: number[],
  contacts?: Contact[]
) => {
  contactIds.forEach((contactId) => {
    queryClient.setQueryData<Contact>(
      [GPanelContactsKeys.GPanelContact, String(contactId)],
      (oldData) => {
        if (!oldData) return oldData;
        const newLabelIds = Array.from(
          new Set([...(oldData.labelIds || []), ...labelIds])
        );
        return {
          ...oldData,
          labelIds: newLabelIds,
        };
      }
    );
  });

  queryClient.setQueryData<Contact[]>(
    [GPanelContactsKeys.OrganizationDirectoryContacts],
    (oldData) => {
      if (!oldData) return oldData;
      return oldData.map((contact) => {
        if (contactIds.includes(Number(contact.id))) {
          const newLabelIds = Array.from(
            new Set([...(contact.labelIds || []), ...labelIds])
          );
          return {
            ...contact,
            labelIds: newLabelIds,
          };
        }
        return contact;
      });
    }
  );

  queryClient.setQueryData<LabelGroupsResponse>(
    [GPanelContactsKeys.LabelGroups],
    (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        labelGroups: oldData.labelGroups.map((group) => ({
          ...group,
          labels: group.labels,
        })),
      };
    }
  );

  labelIds.forEach((labelId) => {
    queryClient.setQueryData<Contact[]>(
      [GPanelContactsKeys.LabelContacts, labelId],
      (oldData) => {
        if (!oldData) return contacts || oldData;
        const existingContactIds = new Set(oldData.map((c) => String(c.id)));
        const newContactsToAdd = (contacts || []).filter((c) =>
          contactIds.includes(Number(c.id))
        );
        const filteredNewContacts = newContactsToAdd.filter(
          (c) => !existingContactIds.has(String(c.id))
        );
        return [...oldData, ...filteredNewContacts];
      }
    );
  });
};

export const updateContactLabelCachesOnRemove = (
  queryClient: QueryClient,
  contactIds: number[],
  labelId: number
) => {
  contactIds.forEach((contactId) => {
    queryClient.setQueryData<Contact>(
      [GPanelContactsKeys.GPanelContact, String(contactId)],
      (oldData) =>
        oldData
          ? {
              ...oldData,
              labelIds: oldData.labelIds?.filter((id) => id !== labelId),
            }
          : oldData
    );
  });

  queryClient.setQueryData<Contact[]>(
    [GPanelContactsKeys.OrganizationDirectoryContacts],
    (oldData) => {
      return (
        oldData?.map((contact) =>
          contactIds.includes(Number(contact.id))
            ? {
                ...contact,
                labelIds: contact.labelIds?.filter((id) => id !== labelId),
              }
            : contact
        ) ?? []
      );
    }
  );

  queryClient.setQueryData<LabelGroupsResponse>(
    [GPanelContactsKeys.LabelGroups],
    (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        labelGroups:
          oldData?.labelGroups?.map((group) => ({
            ...group,
            labels: group.labels,
          })) ?? [],
      };
    }
  );

  queryClient.setQueryData<Contact[]>(
    [GPanelContactsKeys.LabelContacts, labelId],
    (oldData) => {
      if (!oldData) return oldData;
      const contactIdSet = new Set(contactIds.map(String));
      return oldData?.filter(
        (contact) => !contactIdSet.has(String(contact.id))
      );
    }
  );
};
